import { useMemo } from 'react';

import { LIST_GRID_TWO_ROW_MIN_CELLS } from '@/shared/components/itemList/listGrid';
import { useUserFoldersQuery } from '@/shared/features/quiz-folder';
import { useSearchQuizQuery } from '@/shared/quizList/hooks/useSearchQuizQuery';

import {
  buildUserFolderVariables,
  USER_FOLDER_PREVIEW_LIMIT,
} from '../utils/userFolderVariables';

/**
 * 남의 프로필 폴더 섹션 데이터 — 그 사용자의 공개 폴더 첫 페이지.
 *
 * 검색어·정렬은 퀴즈 섹션과 같은 `useSearchQuizQuery` 를 읽는다. 실패하면 섹션만 접는다(퀴즈 목록은 계속 보인다).
 */
export const useUserFolderPreview = (userId: string) => {
  const { searchText, order, period } = useSearchQuizQuery();

  const result = useUserFoldersQuery({
    throwOnError: false,
    retry: false,
    // 실패한 쿼리 때문에 화면이 다시 마운트돼도 즉시 같은 오류를 반복 호출하지 않는다.
    retryOnMount: false,
    variables: buildUserFolderVariables({
      userId,
      searchText,
      order,
      period,
      limit: USER_FOLDER_PREVIEW_LIMIT,
    }),
  });

  const firstPage = result.data?.pages[0];

  return useMemo(() => {
    const totalCount = firstPage?.totalCount ?? 0;
    return {
      folders: firstPage?.items ?? [],
      totalCount,
      isLoading: result.isLoading,
      isError: result.isError,
      /** 가장 좁은 두 줄(4칸)에 다 못 담으면 전체 페이지로 보낸다. */
      hasMore: totalCount > LIST_GRID_TWO_ROW_MIN_CELLS,
    };
  }, [firstPage, result.isLoading, result.isError]);
};
